import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, ListSubheader } from "@mui/material";
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { backendUrl, API_KEYS } from '../config';

const API_URL = backendUrl;

export default function UserManagementPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'user', local_group_id: '', app_group_id: '' });
  const [groups, setGroups] = useState([]);
  const [localGroups, setLocalGroups] = useState([]);
  const [appGroups, setAppGroups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/users/`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => {
        if (!res.ok) throw new Error("Erreur lors du chargement des utilisateurs");
        return res.json();
      })
      .then(data => {
        setUsers(data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${API_URL}/local-groups/`, {
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
          'accept': 'application/json',
        },
      }).then(r => r.data || []),
      axios.get(`${API_URL}/app-groups/`, {
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
          'accept': 'application/json',
        },
      }).then(r => r.data || [])
    ]).then(([locals, apps]) => {
      setLocalGroups(locals);
      setAppGroups(apps);
      setGroups([...locals, ...apps]);
    }).catch(e => {
      console.error('Erreur API:', e);
      setError('Erreur lors du chargement des groupes');
    }).finally(() => setLoading(false));
  }, []);

  const handleOpen = () => {
    setForm({ username: '', password: '', role: 'user', local_group_id: '', app_group_id: '' });
    setFormError(null);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name.endsWith('_id') && value ? parseInt(value) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      if (form.id) {
        // Modification
        const { id, ...toSend } = form;
        await axios.put(`${API_URL}/users/${id}`, toSend, {
          headers: { 
            'x-api-key': API_KEYS.key, 
            'x-api-secret': API_KEYS.secret 
          }
        });
      } else {
        // Ajout
        await axios.post(`${API_URL}/users/`, form, {
          headers: { 
            'x-api-key': API_KEYS.key, 
            'x-api-secret': API_KEYS.secret 
          }
        });
        // --- Créer le répertoire utilisateur user/UUID comme dans explorerApp ---
        let newUserUuid = null;
        try {
          const usersRes = await axios.get(`${API_URL}/users/`, {
            headers: { 
              'x-api-key': API_KEYS.key, 
              'x-api-secret': API_KEYS.secret 
            }
          });
          console.log('Liste utilisateurs:', usersRes.data);

          const createdUser = usersRes.data.find(u => u.username === form.username);
          console.log('Utilisateur trouvé:', createdUser);

          if (createdUser && createdUser.uuid7) {
            console.log('uuid7 trouvé, envoi du POST /create-folder');
            await axios.post(`${API_URL}/create-folder`, {
              uuid7: createdUser.uuid7,
              path: ''
            }, {
              headers: { 
                'x-api-key': API_KEYS.key, 
                'x-api-secret': API_KEYS.secret 
              }
            });
          } else {
            console.error('uuid7 non trouvé pour l\'utilisateur créé');
          }
        } catch (e) {
          console.error('Erreur lors de la création du dossier utilisateur:', e?.response?.data || e);
        }
        // --- Fin création répertoire utilisateur ---
      }
      setOpen(false);
      fetchUsers();
    } catch (err) {
      setFormError('Erreur lors de la soumission');
    }
  };

  const handleEdit = (user) => {
    setForm({
      id: user.id,
      username: user.username,
      password: '', // Pas de préremplissage pour la sécurité
      role: user.role,
      local_group_id: user.local_group_id || '',
      app_group_id: user.app_group_id || ''
    });
    setFormError(null);
    setOpen(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur ${user.username} ?`)) return;
    try {
      const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
      await axios.delete(`${API_URL}/users/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret
        }
      });
      setUsers(users => users.filter(u => u.id !== user.id));
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/users/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error("Erreur lors du chargement des utilisateurs");
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxHeight: '70vh', overflow: 'auto' }}>
      <Typography variant="h5" mb={2}>{t('users.title')}</Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={handleOpen}>
        {t('users.add')}
      </Button>
      {loading ? <CircularProgress /> : error ? <Typography color="error">{error}</Typography> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.username')}</TableCell>
              <TableCell>{t('users.role')}</TableCell>
              <TableCell>{t('users.localGroup')}</TableCell>
              <TableCell>{t('users.appGroup')}</TableCell>
              <TableCell>{t('users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => {
              const groupLocal = localGroups.find(g => g.id === u.local_group_id);
              const groupApp = appGroups.find(g => g.id === u.app_group_id);
              return (
                <TableRow key={u.id}>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>
                    {groupLocal ? (
                      <span>
                        {groupLocal.name}
                        <span style={{
                          marginLeft: 8,
                          fontSize: 12,
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: 6,
                          padding: '2px 8px',
                          background: '#e3f2fd',
                          marginRight: 8
                        }}>Local</span>
                      </span>
                    ) : (<span style={{ color: '#aaa' }}>{t('users.noGroup')}</span>)}
                  </TableCell>
                  <TableCell>
                    {groupApp ? (
                      <span>
                        {groupApp.name}
                        <span style={{
                          marginLeft: 8,
                          fontSize: 12,
                          color: '#43a047',
                          border: '1px solid #43a047',
                          borderRadius: 6,
                          padding: '2px 8px',
                          background: '#e8f5e9',
                          marginRight: 8
                        }}>App</span>
                      </span>
                    ) : (<span style={{ color: '#aaa' }}>{t('users.noGroup')}</span>)}
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => handleEdit(u)} color="primary" size="small">{t('users.edit')}</Button>
                    <Button onClick={() => handleDelete(u)} color="secondary" size="small">{t('users.delete')}</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('users.add')}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} id="add-user-form">
            <TextField
              label={t('users.username')}
              name="username"
              value={form.username}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              label={t('users.password')}
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              select
              label={t('users.role')}
              name="role"
              value={form.role}
              onChange={handleChange}
              fullWidth
              margin="normal"
            >
              <MenuItem value="user">{t('users.roles.user')}</MenuItem>
              <MenuItem value="admin">{t('users.roles.admin')}</MenuItem>
              <MenuItem value="superuser">{t('users.roles.superuser')}</MenuItem>
            </TextField>
            <TextField
              label={t('users.localGroup')}
              name="local_group_id"
              select
              value={form.local_group_id || ''}
              onChange={handleChange}
              fullWidth
              margin="normal"
            >
              <MenuItem value="">{t('users.noGroup')}</MenuItem>
              {localGroups.map(g => (
                <MenuItem value={g.id} key={"local-"+g.id}>{g.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('users.appGroup')}
              name="app_group_id"
              select
              value={form.app_group_id || ''}
              onChange={handleChange}
              fullWidth
              margin="normal"
            >
              <MenuItem value="">{t('users.noGroup')}</MenuItem>
              {appGroups.map(g => (
                <MenuItem value={g.id} key={"app-"+g.id}>{g.name}</MenuItem>
              ))}
            </TextField>
            {formError && <Typography color="error">{formError}</Typography>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('users.cancel')}</Button>
          <Button type="submit" form="add-user-form" variant="contained" color="primary" disabled={creating}>
            {creating ? t('users.creating') : t('users.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
